import { css } from '@styled-system/css';
import { circle, stack } from '@styled-system/patterns';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalysisItemData } from '@/features/analysisMenu/types';
import { PlatformBadge } from '@/shared/components/analysis/PlatformBadge';
import { InteractionOverlay } from '@/shared/components/ui/InteractionOverlay';
import { OptimizedBackgroundImage } from '@/shared/components/ui/OptimizedBackgroundImage';
import { Separator } from '@/shared/components/ui/Separator';

interface AnalysisMenuItemProps {
  analysis: AnalysisItemData;
  isSelected?: boolean;
  isBlurred?: boolean;
  enableMenu?: boolean;
  onClick?: () => void;
  onRename?: (newName: string) => void;
  onStop?: () => void;
  onDelete?: () => void;
}

export const AnalysisMenuItem: FC<AnalysisMenuItemProps> = ({
  analysis,
  isSelected = false,
  isBlurred = false,
  enableMenu = true,
  onClick,
  onRename,
  onStop,
  onDelete,
}) => {
  // v2 : plus de lifecycle — une analyse est complète dès sa création
  const isInProgress = false;

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(analysis.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Handle save
  const handleSave = () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== analysis.name) {
      onRename?.(trimmedValue);
    }
    setIsEditing(false);
    setEditValue(analysis.name);
  };

  // Handle cancel
  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(analysis.name);
  };

  // Handle key press
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  // Handle blur
  const handleBlur = () => {
    handleSave();
  };

  // Start editing
  const startEditing = () => {
    setIsEditing(true);
    setEditValue(analysis.name);
  };

  const containerStyles = stack({
    direction: 'column',
    w: '100%',
    gap: 0,
  });

  const cardStyles = stack({
    direction: 'row',
    h: '3.25rem',
    alignItems: 'center',
    justifyContent: 'space-between',
    w: '100%',
    cursor: 'pointer',
    borderRadius: isSelected ? 'sm' : 0,
    pr: '0.81rem',
    pl: '0.15rem',
    bg: isSelected ? 'bg.selected' : 'transparent',

    _hover: {
      cursor: 'pointer',
      bg: 'bg.selected',
      borderRadius: 'sm',
    },
  });

  const contentStyles = stack({
    direction: 'row',
    alignItems: 'center',
    gap: '0.4rem',
    px: '0.1875rem',
  });

  const analysisProductImageStyles = circle({
    w: '1.875rem',
    h: '1.875rem',
    overflow: 'hidden',
    flexShrink: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    bg: 'bg.selected',
  });

  const infoStyles = stack({
    direction: 'column',
    alignItems: 'flex-start',
    gap: '0.1875rem',
  });

  const nameContainerStyles = stack({
    direction: 'row',
    alignItems: 'center',
    gap: '0.4rem',
  });

  const nameStyles = css({
    fontSize: 'sm',
    fontWeight: 500,
    color: 'font.secondary',
    lineHeight: 1.07,
    textAlign: 'left',
    pb: '0.125rem',
    outline: '1px solid transparent',
    h: '1rem',
  });

  const inputStyles = css({
    fontSize: 'sm',
    fontWeight: 500,
    color: 'font.secondary',
    lineHeight: 1.07,
    textAlign: 'left',
    pb: '0.125rem',
    bg: 'transparent',
    border: 'none',
    outline: '1px solid transparent',
    h: '1rem',
    p: 0,
    m: 0,
    width: '100%',
    fontFamily: 'inherit',

    _focus: {
      outline: '1px solid',
      outlineColor: 'accent.primary',
      borderRadius: 'xs',
      px: 'xs',
    },
  });

  const statusContainerStyles = stack({
    direction: 'row',
    alignItems: 'center',
    gap: '0.0625rem', // 1px
  });

  const statusTextColor = 'font.lowkey';
  const statusTextStyles = css({
    fontSize: '2xs',
    fontWeight: 500,
    lineHeight: 1.07,
    whiteSpace: 'nowrap',
    textAlign: 'center',
    color: statusTextColor,
  });

  const separatorStyles = css({
    alignSelf: 'center',
    w: 'calc(100% - 1rem)',
    mt: '-1px',
  });

  const getStatusText = (): string | null =>
    (analysis.analysedProductCount ?? 0) > 0 ? `${analysis.analysedProductCount} produits analysés !` : null;


  const statusText = getStatusText();

  return (
    <div className={containerStyles}>
      <div
        className={cardStyles}
        onClick={onClick}
        style={isBlurred ? { filter: 'blur(2px)', opacity: 0.6 } : undefined}
      >
        <div className={contentStyles}>
          {/* Analysis Product Image */}
          {analysis.imageUrl ? (
            <OptimizedBackgroundImage className={analysisProductImageStyles} src={analysis.imageUrl} />
          ) : (
            <div className={analysisProductImageStyles} />
          )}

          {/* Info */}
          <div className={infoStyles}>
            {/* Name avec indicateur */}
            <div className={nameContainerStyles}>
              {isEditing ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  className={inputStyles}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <div className={nameStyles}>{analysis.name}</div>
              )}
              <PlatformBadge platform={analysis.platform} />
            </div>

            {/* Status */}
            {statusText && (
              <div className={statusContainerStyles}>
                <div className={statusTextStyles}>{statusText}</div>
              </div>
            )}
          </div>
        </div>
        {enableMenu && (
          <InteractionOverlay
            items={[
              {
                icon: 'rename',
                label: t('analysisMenu.actions.rename'),
                onClick: () => {
                  startEditing();
                },
              },
              isInProgress
                ? {
                    danger: true,
                    icon: 'stop',
                    label: t('analysisMenu.actions.stop'),
                    onClick: () => onStop?.(),
                  }
                : {
                    danger: true,
                    icon: 'trash',
                    label: t('analysisMenu.actions.delete'),
                    onClick: () => onDelete?.(),
                  },
            ]}
          />
        )}
      </div>

      <Separator orientation="horizontal" className={separatorStyles} />
    </div>
  );
};
